import { openDB, type DBSchema } from 'idb';
interface PendingTexts extends DBSchema {
  sends: { key: string; value: { key: string; scope: string; id: string }; indexes: { scope: string } };
}
const database = () =>
  openDB<PendingTexts>('blastoclean-pending-texts', 1, {
    upgrade(db) {
      db.createObjectStore('sends', { keyPath: 'key' }).createIndex('scope', 'scope');
    },
  });
/** The cache authorization fence may change after an upgrade or role refresh;
 * a pending send remains the same effect within this browser deployment. */
export function stableTextIdentityScope(offlineScope: string) {
  const [userId, companyId, locationId] = JSON.parse(offlineScope) as unknown[];
  return JSON.stringify([userId, companyId, locationId]);
}
async function digest(value: unknown) {
  return Array.from(
    new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(value)))),
    (byte) => byte.toString(16).padStart(2, '0'),
  ).join('');
}
/** Read/write transactions coalesce browser-tab races. Only opaque digests and
 * random operation IDs persist; uncertain sends survive reload and sign-out. */
export async function pendingTextIdentity(scope: string, path: string, body: unknown) {
  const logical = body && typeof body === 'object' ? { ...(body as Record<string, unknown>) } : body;
  if (logical && typeof logical === 'object') delete (logical as Record<string, unknown>).version;
  const scopeHash = await digest(scope),
    key = await digest([scopeHash, path, logical]);
  const db = await database(),
    tx = db.transaction('sends', 'readwrite');
  let entry = await tx.store.get(key);
  if (!entry) {
    if ((await tx.store.index('scope').count(scopeHash)) >= 100) {
      tx.abort();
      throw new Error('Review pending texts in the message log before sending more. No text was sent.');
    }
    entry = { key, scope: scopeHash, id: crypto.randomUUID() };
    await tx.store.put(entry);
  }
  await tx.done;
  const id = entry.id;
  return {
    id,
    acknowledge: async () => {
      const update = db.transaction('sends', 'readwrite');
      if ((await update.store.get(key))?.id === id) await update.store.delete(key);
      await update.done;
    },
  };
}
export function containsTextEffect(path: string, body: unknown) {
  const input = body as Record<string, unknown> | undefined;
  return (
    path.endsWith('/send-text') ||
    path.endsWith('/send-both') ||
    (path === '/messages/send' && input?.channel === 'sms') ||
    (path.endsWith('/request-payment') && input?.sendText === true) ||
    (path.endsWith('/setup-link') && input?.text === true)
  );
}

/** Only a positive immediate-send acknowledgement retires a pending identity. */
export function textEffectAcknowledged(path: string, body: unknown) {
  const result = body as Record<string, unknown> | undefined;
  return (
    result?.delivered === true ||
    (path.endsWith('/request-payment') &&
      typeof result?.textedTo === 'string' &&
      result.textedTo.length > 0 &&
      result.textDeferredUntilOpenHours === false &&
      result.textFailed === false &&
      result.textUnconfirmed !== true) ||
    (path.endsWith('/setup-link') && result?.texted === true && result.textDeferred === false)
  );
}
